import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { safeInvalidateSize, safeFitBounds, safeSetView } from "@/lib/leafletSafe";
import { MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

function rate(e) {
  const t = e.items_total || 0;
  return t > 0 ? Math.round(((e.items_restituted || 0) / t) * 100) : 0;
}
function rateColor(r) {
  const h = Math.round((Math.max(0, Math.min(100, r)) / 100) * 140);
  return `hsl(${h}, 70%, 45%)`;
}

export default function EstablishmentsMap({ establishments = [] }) {
  const { t } = useLang();
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);

  const geo = (establishments || []).filter((e) => e.lat != null && e.lng != null);

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: false, zoomControl: true }).setView([46.6, 2.4], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap", maxZoom: 18,
    }).addTo(map);
    mapObj.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => safeInvalidateSize(map), 200);
    return () => { map.remove(); mapObj.current = null; layerRef.current = null; };
  }, [geo.length > 0]); // (re)init once the canvas is mounted (data may arrive after first render)

  useEffect(() => {
    const map = mapObj.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const bounds = [];
    geo.forEach((e) => {
      const r = rate(e);
      const color = e.items_total > 0 ? rateColor(r) : "#94a3b8";
      const radius = 12 + Math.min(16, (e.items_total || 0));
      const m = L.circleMarker([e.lat, e.lng], {
        radius, color, weight: 2, fillColor: color, fillOpacity: 0.55,
      }).addTo(layer);
      m.bindPopup(
        `<b>${e.name}</b>${e.is_primary ? ` · <span style="color:#191A54;font-weight:700">${t("est.primary")}</span>` : ""}<br/>` +
        `${[e.address, e.city].filter(Boolean).join(", ") || ""}<br/>` +
        `<div style="margin-top:6px">` +
        (e.items_total > 0
          ? `<b style="color:${color}">${r}%</b> ${t("map.returnedSuffix").replace("{a}", e.items_restituted).replace("{t}", e.items_total)}`
          : `<span style="color:#94a3b8">${t("map.noItemRegistered")}</span>`) +
        `</div>`
      );
      bounds.push([e.lat, e.lng]);
    });
    map.invalidateSize();
    if (bounds.length === 1) safeSetView(map, bounds[0], 12);
    else if (bounds.length) safeFitBounds(map, bounds, { padding: [50, 50], maxZoom: 12 });
    setTimeout(() => safeInvalidateSize(map), 150);
  }, [establishments]); // eslint-disable-line

  return (
    <div className="mt-6 bg-white rounded-2xl border border-border p-5 card-shadow" data-testid="establishments-map">
      <div className="flex items-center gap-2 mb-1">
        <MapPin size={18} className="text-findo-indigo" />
        <h3 className="font-display font-bold text-slate-900">{t("map.title")}</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        {t("map.legend")}
      </p>
      {geo.length === 0 ? (
        <div className="h-64 grid place-items-center text-sm text-slate-400 bg-slate-50 rounded-xl" data-testid="map-empty">
          {t("map.locating")}
        </div>
      ) : (
        <div ref={mapRef} className="h-80 w-full rounded-xl overflow-hidden z-0" data-testid="map-canvas" />
      )}
      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: rateColor(15) }} /> {t("map.low")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: rateColor(55) }} /> {t("map.medium")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: rateColor(95) }} /> {t("map.high")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-slate-400" /> {t("map.noItems")}</span>
      </div>
    </div>
  );
}
